import React, { ReactElement, FC, useMemo } from 'react'
import { VStack, Avatar, Text, AvatarBadge, Box } from '@chakra-ui/react'
import { useColor } from '../../theme'
import { PersonaAvailability } from './types'
import { useAvailabilityColor } from './useAvailabilityColor'

interface ILargePersonaProps {
  avatarBgColor?: string
  avatarUrl?: string
  title?: ReactElement | string
  subtitle: string
  availability?: PersonaAvailability
}

const avatarSize = '2xl'

/**
 * Large Persona component.
 *
 * Available in only size `2xl`.
 *
 * Invalid or non-provided `avatarUrl` property will result on the display of
 * the username initials.
 *
 * The `title` property can be used to replace the default username.
 *
 */
export const LargePersona: FC<ILargePersonaProps> = ({
  availability,
  avatarBgColor,
  avatarUrl,
  title,
  subtitle,
}) => {
  const titleColor = useColor({ nuance: 5, palette: 'text' })
  const subTitleColor = useColor({ nuance: 6, palette: 'text' })

  const { getAvailabilityBadgeColor } = useAvailabilityColor()

  const avatarAvailability = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return getAvailabilityBadgeColor(availability!)
  }, [availability, getAvailabilityBadgeColor])


  return (
    <VStack marginBottom={4} spacing={6}>
      <Avatar
        bgColor={avatarBgColor}
        data-testid="large-persona-avatar"
        size={avatarSize}
        src={avatarUrl}>
        {availability && (
          <AvatarBadge
            background={avatarAvailability}
            borderWidth={4}
            boxSize={8}
            data-testid="large-persona-availability-badge"
            margin="0.2em"
          />
        )}
      </Avatar>

      <VStack spacing={1}>
        <Box>
          <Text
            color={titleColor}
            data-pii=""
            data-testid="large-persona-title"
            fontSize="2xl"
            fontWeight="700"
            maxWidth={328}
            minHeight={8}
            textAlign='center'>
            {title}
          </Text>
        </Box>
        {subtitle !== '' && (
          <Text
            color={subTitleColor}
            data-pii=""
            data-testid="large-persona-subtitle"
            fontSize="md"
            fontWeight="500"
            maxWidth={400}
            minHeight={6}>
            {subtitle}
          </Text>
        )}
      </VStack>
    </VStack>
  )
}
